import { FormattedMessage } from 'react-intl';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, Typography, IconButton } from '@mui/material';

type DocumentPreviewDialogProps = {
  open: boolean;
  handleClose: () => void;
  documentData: string; // Base64 string
  documentName: string;
};

const DocumentPreviewDialog = ({ open, handleClose, documentData, documentName }: DocumentPreviewDialogProps) => {
  // Strip out prefix if present to clean up base64
  const base64Clean = documentData.includes(',') ? documentData.split(',')[1] : documentData;

  // Detect mime type from file extension
  const extension = documentName.split('.').pop()?.toLowerCase() || '';
  let mimeType = 'application/pdf'; // Default to PDF
  if (['png', 'jpg', 'jpeg', 'gif', 'webp'].includes(extension)) {
    mimeType = `image/${extension === 'jpg' ? 'jpeg' : extension}`;
  }

  const handleDownload = () => {
    try {
      const byteCharacters = atob(base64Clean);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: mimeType });
      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(blob);
      link.download = documentName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Failed to download file', error);
    }
  };

  const isImage = mimeType.startsWith('image/');
  const isPdf = mimeType === 'application/pdf';

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ m: 0, p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6" component="span" noWrap sx={{ maxWidth: '80%' }}>
          {documentName}
        </Typography>
        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={{
            color: (theme) => theme.palette.grey[500],
          }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ p: 0, minHeight: '400px', display: 'flex', flexDirection: 'column' }}>
        {isPdf && (
          <Box sx={{ flexGrow: 1, width: '100%', height: '600px' }}>
            <object
              data={`data:application/pdf;base64,${base64Clean}`}
              type="application/pdf"
              width="100%"
              height="100%">
              <iframe
                src={`data:application/pdf;base64,${base64Clean}`}
                width="100%"
                height="100%"
                title={documentName}
                style={{ border: 'none' }}
              />
            </object>
          </Box>
        )}

        {isImage && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              p: 2,
              backgroundColor: 'background.default',
              flexGrow: 1,
            }}>
            <img
              src={`data:${mimeType};base64,${base64Clean}`}
              alt={documentName}
              style={{
                maxWidth: '100%',
                maxHeight: '60vh',
                objectFit: 'contain',
                borderRadius: '4px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
              }}
            />
          </Box>
        )}

        {!isPdf && !isImage && (
          <Box sx={{ p: 4, textAlign: 'center', flexGrow: 1 }}>
            <Typography variant="body1" sx={{ mb: 2 }}>
              <FormattedMessage
                id="personnelHoliday.previewNotSupported"
                defaultMessage="Bu dosya formatı önizleme için desteklenmiyor. Dosyayı indirerek görüntüleyebilirsiniz."
              />
            </Typography>
            <Button variant="contained" startIcon={<DownloadIcon />} onClick={handleDownload}>
              <FormattedMessage id="personnelHoliday.downloadFile" defaultMessage="Dosyayı İndir" />
            </Button>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button variant="outlined" startIcon={<DownloadIcon />} onClick={handleDownload}>
          <FormattedMessage id="personnelHoliday.downloadFile" defaultMessage="İndir" />
        </Button>
        <Button onClick={handleClose} variant="contained" color="primary">
          <FormattedMessage id="forgotPassword.cancelButtonLabel" defaultMessage="Kapat" />
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DocumentPreviewDialog;
